/**
 * Comprehensive data validation utilities for Cairn storage layer
 *
 * Provides type-safe validation for all data models with proper error handling
 * for offline scenarios and coordinate system integration.
 */

import { isValidPosition, parseCoordinates } from '../lib/coordinates.js'
import type {
  User,
  UserSettings,
  Route,
  Plan,
  Activity,
  Feature,
  LayerGroup,
  PackList,
  PackItem,
  // Legacy types — used by existing code until migrated
  Trip,
  Track,
  Waypoint,
  TripFeature,
} from '../database/schema.js'
import {
  PackListCreateSchema,
  PackListUpdateSchema,
  PackItemCreateSchema,
  PackItemUpdateSchema,
} from '../schemas/pack.js'
import { UserCreateSchema, UserUpdateSchema, UserSettingsPartialSchema } from '../schemas/user.js'
import { RouteCreateSchema, RouteUpdateSchema } from '../schemas/route.js'
import { PlanCreateSchema, PlanUpdateSchema } from '../schemas/plan.js'
import { ActivityCreateSchema, ActivityUpdateSchema } from '../schemas/activity.js'
import {
  FeatureCreateSchema,
  FeatureUpdateSchema,
  FeatureStyleSchema,
  FeatureLabelSchema,
} from '../schemas/feature.js'
import { LayerGroupCreateSchema, LayerGroupUpdateSchema } from '../schemas/layer-group.js'
import { EdgeCreateSchema } from '../schemas/edge.js'
// Legacy schema imports — used by existing code until migrated
import { TripCreateSchema, TripUpdateSchema } from '../schemas/trip.js'
import { TrackCreateSchema, TrackUpdateSchema } from '../schemas/track.js'
import { WaypointCreateSchema, WaypointUpdateSchema } from '../schemas/waypoint.js'
import {
  TripFeatureCreateSchema,
  TripFeatureUpdateSchema,
  FeatureStyleSchema as LegacyFeatureStyleSchema,
  FeatureLabelSchema as LegacyFeatureLabelSchema,
} from '../schemas/trip-feature.js'
import { validateSchema } from '../schemas/validate.js'

/**
 * Validation result type
 */
export interface ValidationResult {
  isValid: boolean
  errors: ValidationError[]
  warnings?: ValidationError[]
}

/**
 * Validation error with context
 */
export interface ValidationError {
  field: string
  message: string
  code: string
  value?: unknown
}

/**
 * Validation options for different contexts
 */
export interface ValidationOptions {
  context?: 'create' | 'update' | 'offline'
  skipOptionalFields?: boolean
  allowPartialCoordinates?: boolean
  strict?: boolean
}

/**
 * Base validation function type
 */
type ValidatorFunction<T> = (data: Partial<T>, options?: ValidationOptions) => ValidationResult

/**
 * Common validation utilities
 */
export class ValidationUtils {
  /**
   * Validate email format
   */
  static isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  /**
   * Validate UUID format
   */
  static isValidUUID(id: string): boolean {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    return uuidRegex.test(id)
  }

  /**
   * Validate ISO date string
   */
  static isValidISODate(dateString: string): boolean {
    try {
      const date = new Date(dateString)
      return date.toISOString() === dateString
    } catch {
      return false
    }
  }

  /**
   * Validate coordinate pair
   */
  static isValidCoordinates(coordinates: [number, number]): boolean {
    // Runtime guard: callers pass external/IndexedDB data that may not match the declared type
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!Array.isArray(coordinates) || coordinates.length !== 2) {
      return false
    }
    const [lat, lng] = coordinates
    return isValidPosition({ lat, lng })
  }

  /**
   * Validate string length
   */
  static isValidStringLength(value: string, min: number, max: number): boolean {
    return typeof value === 'string' && value.length >= min && value.length <= max
  }

  /**
   * Validate positive number
   */
  static isPositiveNumber(value: number): boolean {
    return typeof value === 'number' && !isNaN(value) && value > 0
  }

  /**
   * Validate non-negative number
   */
  static isNonNegativeNumber(value: number): boolean {
    return typeof value === 'number' && !isNaN(value) && value >= 0
  }

  /**
   * Create validation error
   */
  static createError(
    field: string,
    message: string,
    code: string,
    value?: unknown
  ): ValidationError {
    return { field, message, code, value }
  }
}

/**
 * User validation (schema-based via TypeBox + ajv)
 */
export const validateUser: ValidatorFunction<User> = (data, options = {}) => {
  const { context = 'create' } = options
  const schema = context === 'create' ? UserCreateSchema : UserUpdateSchema
  const result = validateSchema(schema, data)
  const errors = [...result.errors]

  if (data.id && !ValidationUtils.isValidUUID(data.id)) {
    errors.push(ValidationUtils.createError('id', 'Invalid UUID format', 'INVALID_FORMAT', data.id))
  }

  if (data.location) {
    const location = data.location
    if (!location.region || !ValidationUtils.isValidStringLength(location.region, 1, 100)) {
      errors.push(
        ValidationUtils.createError(
          'location.region',
          'Region must be 1-100 characters',
          'INVALID_LENGTH'
        )
      )
    }
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- validates external data
    if (!location.coordinates || !ValidationUtils.isValidCoordinates(location.coordinates)) {
      errors.push(
        ValidationUtils.createError(
          'location.coordinates',
          'Invalid coordinate format',
          'INVALID_COORDINATES'
        )
      )
    }
    if (!location.lastUpdated || !ValidationUtils.isValidISODate(location.lastUpdated)) {
      errors.push(
        ValidationUtils.createError('location.lastUpdated', 'Invalid date format', 'INVALID_DATE')
      )
    }
  }

  if (data.bio && !ValidationUtils.isValidStringLength(data.bio, 0, 500)) {
    errors.push(
      ValidationUtils.createError('bio', 'Bio must be 0-500 characters', 'INVALID_LENGTH')
    )
  }

  if (data.certifications) {
    if (!Array.isArray(data.certifications)) {
      errors.push(
        ValidationUtils.createError(
          'certifications',
          'Certifications must be an array',
          'INVALID_TYPE'
        )
      )
    } else {
      data.certifications.forEach((cert, index) => {
        if (!ValidationUtils.isValidStringLength(cert, 1, 50)) {
          errors.push(
            ValidationUtils.createError(
              `certifications[${index}]`,
              'Certification must be 1-50 characters',
              'INVALID_LENGTH'
            )
          )
        }
      })
    }
  }

  return { isValid: errors.length === 0, errors }
}

/**
 * User settings validation (schema-based via TypeBox + ajv)
 */
export const validateUserSettings: ValidatorFunction<UserSettings> = (data, _options = {}) => {
  return validateSchema(UserSettingsPartialSchema, data)
}

/**
 * Route validation (schema-based via TypeBox)
 */
export const validateRoute: ValidatorFunction<Route> = (data, options = {}) => {
  const { context = 'create' } = options
  const schema = context === 'create' ? RouteCreateSchema : RouteUpdateSchema
  return validateSchema(schema, data)
}

/**
 * Plan validation (schema-based via TypeBox)
 *
 * Date range ordering is a cross-field check that stays imperative.
 */
export const validatePlan: ValidatorFunction<Plan> = (data, options = {}) => {
  const { context = 'create' } = options
  const schema = context === 'create' ? PlanCreateSchema : PlanUpdateSchema
  const result = validateSchema(schema, data)
  const errors = [...result.errors]

  // Times are now a flexible array — no cross-field date ordering needed

  if (data.bounds) {
    if (
      !ValidationUtils.isValidCoordinates(data.bounds.ne) ||
      !ValidationUtils.isValidCoordinates(data.bounds.sw)
    ) {
      errors.push(
        ValidationUtils.createError('bounds', 'Invalid bounds coordinates', 'INVALID_COORDINATES')
      )
    }
  }

  return { isValid: errors.length === 0, errors }
}

/**
 * Activity validation (schema-based via TypeBox)
 */
export const validateActivity: ValidatorFunction<Activity> = (data, options = {}) => {
  const { context = 'create' } = options
  const schema = context === 'create' ? ActivityCreateSchema : ActivityUpdateSchema
  const result = validateSchema(schema, data)
  const errors = [...result.errors]

  // Cross-field: time range ordering
  if (data.startTime && data.endTime) {
    if (new Date(data.startTime) >= new Date(data.endTime)) {
      errors.push(
        ValidationUtils.createError(
          'timeRange',
          'End time must be after start time',
          'INVALID_RANGE'
        )
      )
    }
  }

  return { isValid: errors.length === 0, errors }
}

/**
 * Feature validation (schema-based via TypeBox)
 *
 * Geometry coordinates array check and label custom-position conditional
 * stay imperative as they require cross-field logic.
 */
export const validateFeature: ValidatorFunction<Feature> = (data, options = {}) => {
  const { context = 'create' } = options
  const schema = context === 'create' ? FeatureCreateSchema : FeatureUpdateSchema
  const result = validateSchema(schema, data)
  const errors = [...result.errors]

  // Geometry coordinates array check (schema uses Type.Unknown for coordinates)
  if (context === 'create' && data.geometry) {
    if (
      !('coordinates' in data.geometry) ||
      !Array.isArray(
        (data.geometry as GeoJSON.Point | GeoJSON.LineString | GeoJSON.Polygon).coordinates
      )
    ) {
      errors.push(
        ValidationUtils.createError(
          'geometry.coordinates',
          'Coordinates must be an array',
          'INVALID_TYPE'
        )
      )
    }
  }

  if (data.description && !ValidationUtils.isValidStringLength(data.description, 0, 1000)) {
    errors.push(
      ValidationUtils.createError(
        'description',
        'Description must be 0-1000 characters',
        'INVALID_LENGTH'
      )
    )
  }

  // Validate feature style via schema if provided
  if (data.style) {
    const styleResult = validateSchema(FeatureStyleSchema, data.style)
    errors.push(...styleResult.errors)
  }

  // Validate feature label via schema + cross-field custom position check
  if (data.label) {
    const labelResult = validateSchema(FeatureLabelSchema, data.label)
    errors.push(...labelResult.errors)

    if (data.label.position === 'custom' && !data.label.customPosition) {
      errors.push(
        ValidationUtils.createError(
          'label.customPosition',
          'Custom position required when position is custom',
          'REQUIRED_FIELD'
        )
      )
    }

    if (
      data.label.customPosition &&
      !ValidationUtils.isValidCoordinates(data.label.customPosition)
    ) {
      errors.push(
        ValidationUtils.createError(
          'label.customPosition',
          'Invalid custom position coordinates',
          'INVALID_COORDINATES'
        )
      )
    }
  }

  return { isValid: errors.length === 0, errors }
}

/**
 * Edge validation (schema-based via TypeBox)
 */
export const validateEdge = (data: unknown): ValidationResult => {
  return validateSchema(EdgeCreateSchema, data)
}

/**
 * Pack list validation (schema-based via TypeBox + ajv)
 */
export const validatePackList: ValidatorFunction<PackList> = (data, options = {}) => {
  const { context = 'create' } = options
  const schema = context === 'create' ? PackListCreateSchema : PackListUpdateSchema
  return validateSchema(schema, data)
}

/**
 * Pack item validation (schema-based via TypeBox + ajv)
 */
export const validatePackItem: ValidatorFunction<PackItem> = (data, options = {}) => {
  const { context = 'create' } = options
  const schema = context === 'create' ? PackItemCreateSchema : PackItemUpdateSchema
  const result = validateSchema(schema, data)
  const errors = [...result.errors]
  if (data.weight !== undefined && (typeof data.weight !== 'number' || data.weight < 0)) {
    errors.push(
      ValidationUtils.createError('weight', 'Weight must be non-negative', 'INVALID_VALUE')
    )
  }
  if (data.assignedTo && !ValidationUtils.isValidUUID(data.assignedTo)) {
    errors.push(
      ValidationUtils.createError(
        'assignedTo',
        'Invalid assigned user ID',
        'INVALID_FORMAT',
        data.assignedTo
      )
    )
  }
  return { isValid: errors.length === 0, errors }
}

/**
 * Layer group validation (schema-based via TypeBox)
 */
export const validateLayerGroup: ValidatorFunction<LayerGroup> = (data, options = {}) => {
  const { context = 'create' } = options
  const schema = context === 'create' ? LayerGroupCreateSchema : LayerGroupUpdateSchema
  const result = validateSchema(schema, data)
  const errors = [...result.errors]

  if (data.description && !ValidationUtils.isValidStringLength(data.description, 0, 500)) {
    errors.push(
      ValidationUtils.createError(
        'description',
        'Description must be 0-500 characters',
        'INVALID_LENGTH'
      )
    )
  }

  if (data.icon && !ValidationUtils.isValidStringLength(data.icon, 1, 50)) {
    errors.push(
      ValidationUtils.createError('icon', 'Icon must be 1-50 characters', 'INVALID_LENGTH')
    )
  }

  if (data.parentLayerId && !ValidationUtils.isValidUUID(data.parentLayerId)) {
    errors.push(
      ValidationUtils.createError(
        'parentLayerId',
        'Invalid parent layer ID format',
        'INVALID_FORMAT',
        data.parentLayerId
      )
    )
  }

  return { isValid: errors.length === 0, errors }
}

/**
 * Coordinate validation with parsing support
 */
export function validateCoordinateInput(input: string): ValidationResult {
  const errors: ValidationError[] = []

  if (!input || typeof input !== 'string') {
    errors.push(
      ValidationUtils.createError('coordinates', 'Coordinate input is required', 'REQUIRED_FIELD')
    )
    return { isValid: false, errors }
  }

  const position = parseCoordinates(input.trim())
  if (!position) {
    errors.push(
      ValidationUtils.createError(
        'coordinates',
        'Unable to parse coordinate format',
        'INVALID_FORMAT',
        input
      )
    )
    return { isValid: false, errors }
  }

  if (!isValidPosition(position)) {
    errors.push(
      ValidationUtils.createError(
        'coordinates',
        'Coordinates out of valid range',
        'INVALID_RANGE',
        position
      )
    )
    return { isValid: false, errors }
  }

  return { isValid: true, errors: [] }
}

/**
 * Security validation for user input sanitization
 */
export function sanitizeUserInput(input: string): string {
  if (!input || typeof input !== 'string') {
    return ''
  }

  let sanitized = input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
    .replace(/<iframe\b[^>]*>/gi, '')
    .replace(/<object\b[^>]*>/gi, '')
    .replace(/<embed\b[^>]*>/gi, '')

  if (sanitized.length > 10000) {
    sanitized = sanitized.substring(0, 10000)
  }

  return sanitized.trim()
}

/**
 * Validate user input for potential security threats
 */
export function validateUserInputSecurity(input: string): ValidationResult {
  const errors: ValidationError[] = []

  if (!input || typeof input !== 'string') {
    return { isValid: true, errors: [] }
  }

  const xssPatterns = [
    /<script[^>]*>/i,
    /javascript:/i,
    /on\w+\s*=/i,
    /<iframe[^>]*>/i,
    /<object[^>]*>/i,
    /<embed[^>]*>/i,
    /eval\s*\(/i,
    /expression\s*\(/i,
  ]

  for (const pattern of xssPatterns) {
    if (pattern.test(input)) {
      errors.push(
        ValidationUtils.createError(
          'input',
          'Potentially unsafe content detected',
          'SECURITY_VIOLATION',
          input
        )
      )
      break
    }
  }

  if (input.length > 10000) {
    errors.push(
      ValidationUtils.createError(
        'input',
        'Input too long (max 10,000 characters)',
        'INVALID_LENGTH',
        input.length
      )
    )
  }

  const sqlPatterns = [
    /(\bUNION\b|\bSELECT\b|\bINSERT\b|\bUPDATE\b|\bDELETE\b|\bDROP\b)/i,
    /('|(\\')|(;)|(--)|(%27)|(%3D)|(%3B))/i,
  ]

  for (const pattern of sqlPatterns) {
    if (pattern.test(input)) {
      errors.push(
        ValidationUtils.createError(
          'input',
          'Potentially unsafe SQL patterns detected',
          'SECURITY_VIOLATION',
          input
        )
      )
      break
    }
  }

  return { isValid: errors.length === 0, errors }
}

/**
 * Generic validation runner
 */
export function runValidation<T>(
  validator: ValidatorFunction<T>,
  data: Partial<T>,
  options?: ValidationOptions
): ValidationResult {
  try {
    return validator(data, options)
  } catch (error) {
    return {
      isValid: false,
      errors: [
        ValidationUtils.createError('validation', `Validation error: ${error}`, 'VALIDATION_ERROR'),
      ],
    }
  }
}

// ============================================================
// Legacy validators — kept until existing consumers are migrated
// ============================================================

/** @deprecated Use validatePlan instead */
export const validateTrip: ValidatorFunction<Trip> = (data, options = {}) => {
  const { context = 'create' } = options
  const schema = context === 'create' ? TripCreateSchema : TripUpdateSchema
  const result = validateSchema(schema, data)
  const errors = [...result.errors]

  if (data.dateRange?.startDate && data.dateRange.endDate) {
    const startDate = new Date(data.dateRange.startDate)
    const endDate = new Date(data.dateRange.endDate)
    if (startDate >= endDate) {
      errors.push(
        ValidationUtils.createError(
          'dateRange',
          'End date must be after start date',
          'INVALID_RANGE'
        )
      )
    }
  }

  if (
    data.status &&
    !['planning', 'upcoming', 'active', 'completed', 'cancelled'].includes(data.status)
  ) {
    errors.push(
      ValidationUtils.createError('status', 'Invalid status value', 'INVALID_VALUE', data.status)
    )
  }

  if (data.bounds) {
    if (
      !ValidationUtils.isValidCoordinates(data.bounds.ne) ||
      !ValidationUtils.isValidCoordinates(data.bounds.sw)
    ) {
      errors.push(
        ValidationUtils.createError('bounds', 'Invalid bounds coordinates', 'INVALID_COORDINATES')
      )
    }
  }

  return { isValid: errors.length === 0, errors }
}

/** @deprecated Use validateActivity instead */
export const validateTrack: ValidatorFunction<Track> = (data, options = {}) => {
  const { context = 'create' } = options
  const schema = context === 'create' ? TrackCreateSchema : TrackUpdateSchema
  const result = validateSchema(schema, data)
  const errors = [...result.errors]

  if (context === 'create' && !data.geojson) {
    errors.push(ValidationUtils.createError('geojson', 'GeoJSON data is required', 'REQUIRED'))
  }

  if (context === 'create' && data.geojson) {
    try {
      const geojson =
        typeof data.geojson === 'string'
          ? (JSON.parse(data.geojson) as Record<string, unknown>)
          : data.geojson
      if (
        typeof geojson !== 'object' ||
        !('type' in geojson) ||
        geojson.type !== 'FeatureCollection'
      ) {
        errors.push(
          ValidationUtils.createError(
            'geojson',
            'GeoJSON must be a FeatureCollection',
            'INVALID_FORMAT'
          )
        )
      }
    } catch {
      errors.push(
        ValidationUtils.createError('geojson', 'Invalid GeoJSON format', 'INVALID_FORMAT')
      )
    }
  }

  if (data.bounds) {
    if (
      !ValidationUtils.isValidCoordinates(data.bounds.ne) ||
      !ValidationUtils.isValidCoordinates(data.bounds.sw)
    ) {
      errors.push(
        ValidationUtils.createError('bounds', 'Invalid bounds coordinates', 'INVALID_COORDINATES')
      )
    }
  }

  if (data.pointCount !== undefined && !ValidationUtils.isPositiveNumber(data.pointCount)) {
    errors.push(
      ValidationUtils.createError('pointCount', 'Point count must be positive', 'INVALID_VALUE')
    )
  }

  if (data.distance !== undefined && !ValidationUtils.isNonNegativeNumber(data.distance)) {
    errors.push(
      ValidationUtils.createError('distance', 'Distance must be non-negative', 'INVALID_VALUE')
    )
  }

  return { isValid: errors.length === 0, errors }
}

/** @deprecated Use validateFeature instead */
export const validateWaypoint: ValidatorFunction<Waypoint> = (data, options = {}) => {
  const { context = 'create' } = options
  const schema = context === 'create' ? WaypointCreateSchema : WaypointUpdateSchema
  const result = validateSchema(schema, data)
  const errors = [...result.errors]
  if (data.elevation !== undefined && typeof data.elevation !== 'number') {
    errors.push(
      ValidationUtils.createError('elevation', 'Elevation must be a number', 'INVALID_TYPE')
    )
  }
  return { isValid: errors.length === 0, errors }
}

/** @deprecated Use validateFeature instead */
export const validateTripFeature: ValidatorFunction<TripFeature> = (data, options = {}) => {
  const { context = 'create' } = options
  const schema = context === 'create' ? TripFeatureCreateSchema : TripFeatureUpdateSchema
  const result = validateSchema(schema, data)
  const errors = [...result.errors]

  if (context === 'create' && data.geometry) {
    if (
      !('coordinates' in data.geometry) ||
      !Array.isArray(
        (data.geometry as GeoJSON.Point | GeoJSON.LineString | GeoJSON.Polygon).coordinates
      )
    ) {
      errors.push(
        ValidationUtils.createError(
          'geometry.coordinates',
          'Coordinates must be an array',
          'INVALID_TYPE'
        )
      )
    }
  }

  if (data.description && !ValidationUtils.isValidStringLength(data.description, 0, 1000)) {
    errors.push(
      ValidationUtils.createError(
        'description',
        'Description must be 0-1000 characters',
        'INVALID_LENGTH'
      )
    )
  }

  if (data.layerId && !ValidationUtils.isValidUUID(data.layerId)) {
    errors.push(
      ValidationUtils.createError(
        'layerId',
        'Invalid layer ID format',
        'INVALID_FORMAT',
        data.layerId
      )
    )
  }

  if (data.style) {
    const styleResult = validateSchema(LegacyFeatureStyleSchema, data.style)
    errors.push(...styleResult.errors)
  }

  if (data.label) {
    const labelResult = validateSchema(LegacyFeatureLabelSchema, data.label)
    errors.push(...labelResult.errors)

    if (data.label.position === 'custom' && !data.label.customPosition) {
      errors.push(
        ValidationUtils.createError(
          'label.customPosition',
          'Custom position required when position is custom',
          'REQUIRED_FIELD'
        )
      )
    }

    if (
      data.label.customPosition &&
      !ValidationUtils.isValidCoordinates(data.label.customPosition)
    ) {
      errors.push(
        ValidationUtils.createError(
          'label.customPosition',
          'Invalid custom position coordinates',
          'INVALID_COORDINATES'
        )
      )
    }
  }

  return { isValid: errors.length === 0, errors }
}
